import type { NextArrivalDto } from "../types/dtos/api/stop-platform.dto";
import { hexToRgb } from "./ColorTools";
import {
    getRentalLegColorMode,
    getTransportModeGroup,
    TransportModeGroup,
    type ItineraryLegTransportSignals,
} from "./TransportModeGroupsTools";

type ModeOrGroup = TransportModeGroup | string | null | undefined;

const DEFAULT_GROUP = TransportModeGroup.UNKNOWN;

const GROUP_HEX_COLOR_MAP: Record<TransportModeGroup, string> = {
    [TransportModeGroup.WALK]:    "#2ECC71",
    [TransportModeGroup.BICYCLE]: "#2ECC71",
    [TransportModeGroup.RENTAL]:  "#2ECC71",

    [TransportModeGroup.MY_CAR]: "#F92B14",
    [TransportModeGroup.CAR]:    "#F92B14",

    [TransportModeGroup.TRAIN]: "#3498DB",
    [TransportModeGroup.TRAM]:  "#3498DB",

    [TransportModeGroup.BUS]: "#E67E22",

    [TransportModeGroup.BOAT]: "#335DC2",

    [TransportModeGroup.BLABLADAILY]: "#4CD244",
    [TransportModeGroup.BLABLACAR]:   "#3579F6",
    [TransportModeGroup.HE_LEMAN]:    "#B01C97",

    [TransportModeGroup.UNSUPPORTED_LIFT]: "#000",
    [TransportModeGroup.UNKNOWN]:          "#000",
};

/*
 *const GROUP_HEX_COLOR_MAP: Record<TransportModeGroup, string> = {
 *    [TransportModeGroup.WALK]:      "#2ECC71",
 *    [TransportModeGroup.BICYCLE]:   "#2ECC71",
 *    [TransportModeGroup.RENTAL]:   "#2ECC71",
 *
 *    [TransportModeGroup.MY_CAR]: "#E74C3C",
 *    [TransportModeGroup.CAR]:    "#E74C3C",
 *
 *    [TransportModeGroup.TRAIN]: "#3498DB",
 *    [TransportModeGroup.TRAM]:  "#3498DB",
 *
 *    [TransportModeGroup.BUS]: "#F1C40F",
 *
 *    [TransportModeGroup.BLABLADAILY]: "#E67E22", // #CE1EB1
 *    [TransportModeGroup.BLABLACAR]:   "#E67E22", // #CE1EB1
 *    [TransportModeGroup.HE_LEMAN]:    "#E67E22", // #CE1EB1
 *
 *    [TransportModeGroup.UNSUPPORTED_LIFT]: "#000",
 *    [TransportModeGroup.UNKNOWN]:          "#000",
 *};
 */

const isTransportModeGroup = ( value: string ): value is TransportModeGroup => {
    return Object.values( TransportModeGroup ).includes( value as TransportModeGroup );
};

const resolveTransportModeGroup = ( modeOrGroup: ModeOrGroup ): TransportModeGroup => {
    if ( typeof modeOrGroup === "string" && isTransportModeGroup( modeOrGroup ) ) {
        return modeOrGroup;
    }

    return getTransportModeGroup( modeOrGroup );
};

export const getTransportModeGroupColorHex = ( modeOrGroup: ModeOrGroup ): string => {
    const group = resolveTransportModeGroup( modeOrGroup );

    return GROUP_HEX_COLOR_MAP[group] ?? GROUP_HEX_COLOR_MAP[DEFAULT_GROUP];
};

/**
 * Hex palette color for an itinerary leg, including rental form-factor overrides (for example car-sharing → car red).
 *
 * @param mode Itinerary leg mode.
 * @param rentalVehicleType Optional `leg.rental.vehicle.type` for shared-mobility legs.
 * @returns CSS hex color for the leg.
 */
export const getItineraryLegColorHex = ( leg: ItineraryLegTransportSignals ): string => {
    return getTransportModeGroupColorHex( getRentalLegColorMode( leg ) );
};

/**
 * CSS `rgba(...)` for an itinerary leg using the same rental-aware palette rules as {@link getItineraryLegColorHex}.
 *
 * @param mode Itinerary leg mode.
 * @param rentalVehicleType Optional `leg.rental.vehicle.type` for shared-mobility legs.
 * @param opacity Alpha component in `[0, 1]`.
 * @returns CSS rgba color string.
 */
export const getItineraryLegColorRgba = (
    leg: ItineraryLegTransportSignals,
    opacity: number,
): string => {
    const hex = getItineraryLegColorHex( leg );
    const rgb = hexToRgb( hex );

    return `rgba(${ rgb.r }, ${ rgb.g }, ${ rgb.b }, ${ normalizeOpacity( opacity ) })`;
};

const normalizeOpacity = ( opacity: number ): number => {
    if ( opacity < 0 ) {
        return 0;
    }
    if ( opacity > 1 ) {
        return 1;
    }

    return opacity;
};

/**
 * CSS `rgba(...)` from a {@link NextArrivalDto} GTFS `route_color` hex.
 * When `nextArrival` is null, uses the parse fallback RGB (same as missing/invalid hex).
 * When `opacity` is omitted, alpha is 1 (opaque). Alpha is clamped to [0, 1].
 *
 * For palette colors by transport mode, use {@link getTransportModeGroupColorRgba} instead.
 */
export const getNextArrivalColorRgba = (
    nextArrival: NextArrivalDto | null,
    opacity: number = 1,
): string => {
    const colorHex = nextArrival?.routeColor ?? null;
    const { r, g, b } = hexToRgb( colorHex );

    return `rgba(${ r }, ${ g }, ${ b }, ${ normalizeOpacity( opacity ) })`;
};

export const getTransportModeGroupColorRgba = (
    modeOrGroup: ModeOrGroup,
    opacity: number,
): string => {
    const hex = getTransportModeGroupColorHex( modeOrGroup );
    const rgb = hexToRgb( hex );

    return `rgba(${ rgb.r }, ${ rgb.g }, ${ rgb.b }, ${ normalizeOpacity( opacity ) })`;
};
